import React from 'react'
import Head from 'next/head'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FaWrench } from '@react-icons/all-files/fa/FaWrench'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import { SHOW_POPUP } from '../../store/slices/ui'
import { openStageBuilderPopup } from '../../components/StageBuilderComp'
import QuotePop from '../../components/DeferredQuotePopup'
import ClosingActionBand from '../../components/design-system/marketing/ClosingActionBand'
import EquipmentGuide from '../../components/design-system/marketing/EquipmentGuide'
import EventShowcase from '../../components/design-system/marketing/EventShowcase'
import FAQGroup from '../../components/design-system/marketing/FAQGroup'
import MediaFeature from '../../components/design-system/marketing/MediaFeature'
import PageShell from '../../components/design-system/foundations/PageShell'
import ProductCatalog from '../../components/design-system/commerce/ProductCatalog'
import RelatedServices from '../../components/design-system/marketing/RelatedServices'
import ReviewProof from '../../components/design-system/marketing/ReviewProof'
import ServiceHero from '../../components/design-system/marketing/ServiceHero'
import ServiceOverview from '../../components/design-system/marketing/ServiceOverview'
import { BUSINESS_HOURS } from '../../content/businessHours'
import orlandoStageHero from '../../public/Pictures/categories-v2/orlando-stage-rental-hero-v2.jpg'
import stageSelection from '../../public/Pictures/stage-orlando.jpg'
import stageOutdoor from '../../public/Pictures/stage-outdoor.jpg'
import stageWedding from '../../public/Pictures/stage-platform.jpg'
import stagePortable from '../../public/Pictures/portable-stage.jpg'
import completeStageSystem from '../../public/Pictures/categories-v2/staging-stage-bundles-finished-v2.jpg'
import * as gtag from '../../lib/gtag'
import { compactCatalogProducts } from '../../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../../utilities/eventPublicPayload'
import { isProductAvailableInMarket } from '../../utilities/fulfillmentMarket'
import { getEventServices } from '../../utilities/portfolioPresentation'
import { requestPageData } from '../../utilities/boundedPageData'

const PAGE_PATH = '/orlando/stage-rental'
const CANONICAL_URL = `https://miamisoundrental.com${PAGE_PATH}`
const ORLANDO_REVIEWS_URL = 'https://search.google.com/local/reviews?placeid=ChIJb8Yw7KN754gR8I3KJ8cvK8o'
const TITLE = 'Stage Rental Orlando & Tampa | Modular Stages & Setup'
const DESCRIPTION = 'Rent modular stages, stage platforms and risers in Orlando and Tampa. Compare stage sizes or configure a custom footprint with Central Florida delivery and setup.'

const stageSelectionContent = {
  id: 'orlando-stage-selection-guide',
  eyebrow: 'Choose the physical stage first',
  heading: 'Match the footprint, height and site conditions to the event',
  body: [
    'A useful stage plan starts with the people and equipment that must fit on the platform—not with a generic package name. We configure modular decks and risers around the required footprint, finished height and venue access.',
    'Sound, lighting, video walls and truss are separate systems. They can be coordinated when needed without turning every stage rental into a full production package.'
  ],
  guide: {
    eyebrow: 'Decision support',
    heading: 'What we confirm before delivery',
    summary: 'The same footprint can require a different supporting plan depending on height, surface and access.',
    body: [
      <p key='footprint'><strong>Footprint and use</strong><br />Allow room for speakers, panels, performers, instruments, DJ furniture or a runway—not only the visible activity at center stage.</p>,
      <p key='height'><strong>Height and sightlines</strong><br />Low risers suit presentations and ceremonies; larger rooms and performance programs may need a taller platform, stairs and guardrails.</p>,
      <p key='site'><strong>Surface, access and safety</strong><br />Indoor floors, pavement, grass and constrained load-in routes affect leveling, stairs, skirting and the final setup plan.</p>
    ]
  },
  media: { src: stageSelection, alt: 'Portable modular stage configured for an Orlando event' }
}

const pricingContent = {
  id: 'orlando-stage-rental-pricing',
  eyebrow: 'Transparent stage pricing',
  heading: 'The platform price is only one part of the final stage plan',
  body: [
    'Package prices cover the listed stage equipment. The final order also reflects the rental duration, delivery distance, setup schedule and any supporting components required by the chosen height and site.',
    'Enter the event dates and location before booking so the order can confirm local Orlando availability, travel to Tampa when applicable, and the labor needed for assembly and collection.'
  ],
  guide: {
    heading: 'Main pricing factors',
    body: [
      <p key='size'><strong>Size and height</strong><br />More decks, taller legs and wider footprints increase equipment and handling requirements.</p>,
      <p key='safety'><strong>Stairs, rails and finish</strong><br />Stage height and use determine whether stairs, guardrails and skirting are required.</p>,
      <p key='delivery'><strong>Dates, access and travel</strong><br />Rental duration, venue access, delivery distance and setup windows determine the final service price.</p>
    ]
  },
  media: { src: stageWedding, alt: 'Finished modular stage platform prepared for an event' }
}

const stageUseCases = [
  {
    eyebrow: 'Presentations · graduations · ceremonies',
    title: 'Presentation and graduation stages',
    summary: 'Raised platforms that improve sightlines without adding unnecessary production systems.',
    body: <p>Choose a compact speaking riser or a wider platform for panels, awards and school ceremonies. Stairs, rails and skirting are matched to the height and venue.</p>,
    image: stageSelection,
    imageAlt: 'Portable stage platform for a presentation in Central Florida'
  },
  {
    eyebrow: 'Bands · DJs · live programs',
    title: 'Band and performance stages',
    summary: 'Working room for performers, instruments, backline and safe movement.',
    body: <p>Start with the performance footprint. Sound, lighting and video can then be scoped as separate systems when the program needs them.</p>,
    image: stagePortable,
    imageAlt: 'Portable modular stage used for a live program'
  },
  {
    eyebrow: 'Weddings · parties · private events',
    title: 'Wedding and private-event stages',
    summary: 'Clean, finished platforms for entertainment, ceremonies and focal moments.',
    body: <p>Use low risers for a DJ or ceremony, or a wider stage for live entertainment. Skirting, stairs and access are planned with the room layout.</p>,
    image: stageWedding,
    imageAlt: 'Finished modular platform for a private event'
  },
  {
    eyebrow: 'Outdoor programs',
    title: 'Portable outdoor stage platforms',
    summary: 'Modular decks assembled on site for speeches, bands, community events and ceremonies.',
    body: <p>We review the surface, leveling, weather plan and safe access. These are modular platforms—not trailer-based or hydraulic mobile stages.</p>,
    image: stageOutdoor,
    imageAlt: 'Portable modular stage assembled outdoors'
  }
]

const requiredStageQuestions = [
  {
    question: 'What affects stage rental cost in Orlando or Tampa?',
    answer: 'Stage rental cost depends on the footprint, finished height, rental duration, stairs, guardrails, skirting, site surface, venue access, delivery location and setup schedule. Choose a package to see equipment pricing, then enter the date and location for the final delivery and labor price.'
  },
  {
    question: 'What is included with a stage rental?',
    answer: 'This page covers the physical modular stage: decks or risers and the supporting components required for the selected layout. Stairs, guardrails and skirting are added when the height and use require them. Sound, lighting, video walls and truss are separate services.'
  },
  {
    question: 'Do you rent trailer-based or hydraulic mobile stages?',
    answer: 'No. Miami Sound Rental offers portable modular stage platforms assembled at the venue. We do not offer stages on wheels, trailer stages or hydraulic mobile stages.'
  },
  {
    question: 'Can a modular stage be installed outdoors?',
    answer: 'Yes, when the site and weather conditions allow safe setup. We review the ground surface, leveling, stairs, guardrails, load-in access, forecast and venue requirements before confirming an outdoor stage.'
  },
  {
    question: 'How do I choose the right stage size?',
    answer: 'List everyone and everything that must fit on the platform, then allow working room and safe movement. Speakers and ceremonies may need a compact riser, while bands, DJs, runways and multi-person programs need a wider or longer footprint.'
  },
  {
    question: 'Do you deliver stage rentals from Orlando to Tampa?',
    answer: 'Yes, eligible stage rentals can be delivered and assembled in Tampa and throughout Central Florida. The final price depends on the date, travel distance, venue access and setup requirements.'
  },
  {
    question: 'Are stage platforms and stage risers the same service?',
    answer: 'They use the same modular deck system. A stage riser is usually a smaller or lower platform, while a stage rental may combine more decks into a larger footprint. This page lets you compare both uses without creating competing pages for the same equipment.'
  }
]

const normalizeFaq = questions => {
  const normalized = (Array.isArray(questions) ? questions : [])
    .map(question => ({
      ...question,
      question: question?.question || question?.title || question?.name,
      answer: question?.answer || question?.answerContent || question?.body
    }))
    .filter(question => question.question && question.answer)

  return [...normalized, ...requiredStageQuestions].filter((question, index, array) => (
    array.findIndex(candidate => candidate.question.toLowerCase() === question.question.toLowerCase()) === index
  ))
}

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [landingResponse, productResponse, faqResponse, reviewResponse] = await Promise.all([
    requestPageData({
      url: `${SERVER_IP}/user/static-landing`,
      fallback: {
        newsletterevents: [],
        total_events: null
      },
      label: 'Orlando stage rental events'
    }),
    requestPageData({
      required: true,
      validateData: Array.isArray,
      url: `${SERVER_IP}/inventory/product`,
      params: {
        msr: true,
        cat: 'Stage-Bundles'
      },
      label: 'Orlando stage rental catalog'
    }),
    requestPageData({
      url: `${SERVER_IP}/faq`,
      params: {
        category: 'Stage'
      },
      fallback: [],
      label: 'Orlando stage rental FAQs'
    }),
    requestPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: {
        page: PAGE_PATH,
        limit: 3
      },
      fallback: null,
      label: 'Orlando stage rental reviews'
    })
  ])

  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(productResponse.data) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const { newsletterevents = [], total_events = null } = landingResponse.data || {}
  const usedFallback = [landingResponse, productResponse, faqResponse, reviewResponse]
    .some(response => response.usedFallback)

  return {
    props: {
      faq: faqResponse.data,
      newsletterevents: compactPublicEvents(newsletterevents),
      reviewData: reviewResponse.data,
      total_events
    },
    revalidate: usedFallback ? 60 : 3600
  }
})

const StageRental = ({ faq, newsletterevents = [], reviewData, total_events }) => {
  const dispatch = useDispatch()
  const products = useSelector(getFilteredProducts)
  const stageProducts = products.filter(product => (
    product.categories?.some(category => /staging\/stage bundles/i.test(category))
  ))
  const localStageProducts = stageProducts.filter(product => isProductAvailableInMarket(product, 'Orlando'))
  const stageEvents = newsletterevents.filter(event => getEventServices(event).includes('Staging'))
  const faqQuestions = normalizeFaq(faq)

  const openStageBuilder = () => {
    gtag.event({ action: 'stage_builder_click', label: 'leads', value: 2, category: 'orders' })
    openStageBuilderPopup({ dispatch })
  }
  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const primaryAction = {
    label: 'Browse Orlando Stage Packages',
    href: '#catalog',
    icon: FiShoppingBag,
    conversionKey: 'orlando-stage-rental.hero.browse-stages',
    channel: 'rental',
    salesPath: 'self_service',
    focusTargetOnNavigate: true,
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }
  const builderAction = {
    label: 'Configure a Custom Stage',
    icon: FaWrench,
    conversionKey: 'orlando-stage-rental.hero.build-stage',
    channel: 'builder',
    salesPath: 'self_service',
    onClick: openStageBuilder
  }
  const quoteAction = {
    label: 'Ask About Delivery & Setup',
    icon: FiMessageCircle,
    conversionKey: 'orlando-stage-rental.closing.delivery-quote',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Stage Rental in Orlando and Tampa',
    serviceType: 'Modular stage platform and stage riser rental',
    url: CANONICAL_URL,
    description: DESCRIPTION,
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    provider: { '@type': 'LocalBusiness', name: 'Miami Sound Rental Orlando', url: 'https://miamisoundrental.com/orlando' },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando stage rental packages',
      itemListElement: localStageProducts
        .filter(product => Number.isFinite(Number(product.price?.rental)))
        .slice(0, 12)
        .map(product => ({
          '@type': 'Offer',
          price: product.price.rental,
          priceCurrency: 'USD',
          itemOffered: { '@type': 'Service', name: product.name, description: product.description }
        }))
    }
  }

  return (
    <>
      <BreadcrumbJsonLd itemListElements={[
        { position: 1, name: 'Home', item: 'https://miamisoundrental.com' },
        { position: 2, name: 'Orlando', item: 'https://miamisoundrental.com/orlando' },
        { position: 3, name: 'Stage Rental', item: CANONICAL_URL }
      ]} />
      <NextSeo
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL_URL}
        twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
        openGraph={{
          type: 'website',
          url: CANONICAL_URL,
          title: TITLE,
          description: DESCRIPTION,
          images: [{
            url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-stage-rental-hero-v2.jpg',
            width: 1600,
            height: 900,
            alt: 'Portable modular stage rental in Orlando'
          }],
          site_name: 'Miami Sound Rental'
        }}
        additionalMetaTags={[{
          name: 'keywords',
          content: 'stage rental Orlando, stage rental Tampa, portable stage rental Orlando, modular stage rental, stage platform rental, stage riser rental, outdoor stage rental Central Florida'
        }]}
      />
      <LocalBusinessJsonLd
        type='Store'
        id='https://miamisoundrental.com/orlando'
        name='Miami Sound Rental Orlando'
        description='Audiovisual equipment rental and event production services in Orlando and Central Florida'
        url='https://miamisoundrental.com/orlando'
        telephone='+14078070052'
        address={{ streetAddress: '8600 Commodity Cir #152', addressLocality: 'Orlando', addressRegion: 'FL', postalCode: '32819', addressCountry: 'US' }}
        geo={{ latitude: '28.445134', longitude: '-81.431191' }}
        images={[
          'https://miamisoundrental.com/Pictures/MSR_cover.jpg',
          'https://miamisoundrental.com/Pictures/categories-v2/orlando-stage-rental-hero-v2.jpg'
        ]}
        openingHours={BUSINESS_HOURS}
        areaServed={[{
          geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
          geoRadius: '160000'
        }]}
      />
      <Head>
        <script
          key='orlando-stage-rental-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>

      <PageShell>
        <ServiceHero
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Orlando', href: '/orlando' },
            { label: 'Stage Rental' }
          ]}
          eyebrow='Modular stage rental · Orlando, Tampa & Central Florida'
          title='Stage Rental in Orlando & Tampa'
          description='Compare portable modular stages, platforms and risers by footprint and height, or configure a custom stage for presentations, bands, weddings and outdoor events. Central Florida delivery and assembly are available.'
          image={{ src: orlandoStageHero, position: '70% center' }}
          imageAlt='Portable modular stage assembled for an Orlando event'
          primaryAction={primaryAction}
          secondaryAction={builderAction}
          stackActionsOnMobile
          processLabel='How stage rental works'
          steps={[
            { icon: 'search', title: 'Choose the footprint', description: 'Compare local packages or configure exact dimensions.' },
            { icon: 'calendar', title: 'Select dates', description: 'Tell us when and where the stage is needed.' },
            { icon: 'truck', title: 'Delivery & assembly', description: 'Our crew installs the modular platform on site.' },
            { icon: 'success', title: 'Event ready', description: 'Receive the confirmed platform and safety components.' }
          ]}
        />

        <ProductCatalog
          carousel={{ name: 'Orlando Stage Rentals', url: CANONICAL_URL, description: 'Portable modular stages, platforms and risers available from Orlando' }}
          filters={[
            ['Light & Portable', 'IntelliStage'],
            ['Heavy Duty', 'MSR'],
            ['Acrylic', 'Acrylic']
          ]}
          sortOptions={['Capacity', 'Area', 'Height']}
          listName='Stage Packages'
          anchorId='catalog'
          eyebrow='Local Orlando stage inventory'
          description='Orlando availability is shown first. Choose All only when you want to compare equipment that may require fulfillment from Miami and additional travel.'
          headerAction={{ ...builderAction, label: 'Build a Custom Footprint' }}
          inventoryMarket='Orlando'
          products={stageProducts}
        />

        <ServiceOverview content={stageSelectionContent} />
        <EquipmentGuide
          eyebrow='Choose by event use'
          headingId='orlando-stage-rental-by-event-type'
          title='Stage Rental by Event Type'
          description='The event type suggests a starting footprint, but the number of people, equipment, height, site and access determine the final stage.'
          items={stageUseCases}
        />
        <ServiceOverview content={pricingContent} />

        <MediaFeature
          eyebrow='Service boundary'
          heading='Portable modular stages—not stages on wheels'
          body={(
            <>
              <p>We assemble stage decks and risers at the venue, then add stairs, guardrails and skirting when the confirmed height and use require them.</p>
              <p>Miami Sound Rental does not offer trailer-based or hydraulic mobile stages. Sound, lighting, video walls and truss remain separate specialist services unless you request a coordinated production scope.</p>
            </>
          )}
          secondaryHeading='Available across Central Florida'
          secondaryBody={<p>Orlando inventory serves local events first, with eligible delivery and setup available in Tampa and surrounding Central Florida markets.</p>}
          image={completeStageSystem}
          imageAlt='Finished modular stage platform with a professional event backdrop'
          imagePosition='center'
          framed
        />

        <RelatedServices
          eyebrow='Add only the systems the event needs'
          heading='Complete the stage without confusing page ownership'
          description='Use these specialist pages when the request extends beyond the physical platform.'
          headingId='orlando-stage-related-services'
          additionalServices={[
            { h1: 'Stage Platforms & Risers', url: '#catalog', miniText: 'Return to the Orlando stage catalog and compare modular platform sizes.' },
            { h1: 'Stage Truss Rental', url: '/staging/truss', miniText: 'Plan ground-supported or overhead truss separately from the stage platform.' },
            { h1: 'Pipe & Drape Rental', url: '/staging/drape', miniText: 'Add backdrops, room divisions and finished event boundaries.' },
            { h1: 'Stage Skirting & Accessories', url: '/staging/accessories', miniText: 'Match skirting, stairs and approved accessories to the physical stage.' },
            { h1: 'Orlando Speaker Rental', url: '/orlando/speaker-rental', miniText: 'Plan audience coverage, microphones and sound separately from the stage.' },
            { h1: 'Orlando LED Video Wall Rental', url: '/orlando/led-video-wall-rental', miniText: 'Add locally available LED display systems for presentations and events.' }
          ]}
        />

        <FAQGroup
          questions={faqQuestions}
          title='Orlando Stage Rental Questions'
          headingId='orlando-stage-rental-faq-title'
          eyebrow='Planning and pricing answers'
          intro='Clear answers about stage cost, sizing, Tampa delivery, outdoor use and the difference between modular platforms and mobile trailer stages.'
        />
        <EventShowcase
          actionLabel='View More Stage Production Work'
          description='These projects show physical stages in the context of real presentations, private events, concerts and outdoor programs.'
          events={stageEvents}
          heading='Stages in real event environments'
          eyebrow='Recent stage work'
          sectionId='orlando-stage-events'
          totalEvents={total_events}
        />
        <ReviewProof
          filter={['convenient', 'empower']}
          heading='Recent feedback about delivery, setup and event support'
          initialData={reviewData}
          pagePath={PAGE_PATH}
          reviewsUrl={ORLANDO_REVIEWS_URL}
          sectionId='orlando-stage-rental-reviews'
        />
        <ClosingActionBand
          ariaLabel='Orlando stage rental options'
          eyebrow='Choose the stage path that fits'
          heading='Ready to plan the physical stage?'
          description='Configure an exact modular footprint, or ask our team about Central Florida delivery, setup and a coordinated production scope.'
          headingId='orlando-stage-rental-closing-action'
          proofItems={[
            'Orlando inventory shown first',
            'Footprint, height and site conditions reviewed together',
            'Delivery and on-site assembly available across Central Florida'
          ]}
          primaryAction={builderAction}
          secondaryAction={quoteAction}
        />
      </PageShell>
    </>
  )
}

export default StageRental
